import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Settings,
  ArrowLeft,
  ShieldCheck,
  Server,
  Database,
  Radio,
  Eye,
  Lock,
} from "lucide-react";

export default function AdminSettingsSection({ onBack }) {
  const { data: members = [] } = useQuery({
    queryKey: ["admin_members"],
    queryFn: () => base44.entities.User.list(),
  });

  const admins = members.filter((m) => m.role === "admin");
  const trainers = members.filter((m) => m.role === "trainer");

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
          aria-label="Back to Command Center"
        >
          <ArrowLeft className="w-4 h-4 text-sky-400" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-white leading-tight">Platform Settings</h1>
          <p className="text-[11px] text-slate-400">System configuration, roles & audit status</p>
        </div>
      </div>

      {/* System Health Status */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 shadow-md space-y-3">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-sky-400" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-white">System Infrastructure</h2>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
            <span className="text-[10px] text-slate-400 block">Database</span>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="font-bold text-white text-[11px]">Connected</span>
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
            <span className="text-[10px] text-slate-400 block">API Backend</span>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="font-bold text-white text-[11px]">FastAPI Ready</span>
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
            <span className="text-[10px] text-slate-400 block">Video Sessions</span>
            <div className="flex items-center gap-1.5 mt-1">
              <Radio className="w-3 h-3 text-sky-400" />
              <span className="font-bold text-white text-[11px]">Agora RTC</span>
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
            <span className="text-[10px] text-slate-400 block">Vision Scanner</span>
            <div className="flex items-center gap-1.5 mt-1">
              <Eye className="w-3 h-3 text-purple-400" />
              <span className="font-bold text-white text-[11px]">AI Vision Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Authorized Administrators */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 shadow-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-white">System Administrators</h2>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] font-bold">
            {admins.length} Admin{admins.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="space-y-2">
          {admins.map((adm) => {
            const initials = adm.full_name
              ? adm.full_name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)
              : (adm.email?.[0] || "?").toUpperCase();

            return (
              <div
                key={adm.id}
                className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-purple-500/20 text-purple-300 font-bold text-xs flex items-center justify-center shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-white truncate">
                      {adm.full_name || "Admin"}
                    </p>
                    <p className="text-[10px] text-slate-400 truncate">{adm.email}</p>
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 text-[9px] font-bold uppercase tracking-wider shrink-0">
                  Super Admin
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Role Permissions Info */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 shadow-md space-y-2.5">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-sky-400" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-white">Role Hierarchy</h2>
        </div>

        <div className="space-y-2 text-[11px] text-slate-300">
          <div className="p-2 rounded-xl bg-white/5 border border-white/5">
            <span className="font-bold text-sky-400">Admin ({admins.length})</span>
            <p className="text-slate-400 text-[10px] mt-0.5">
              Full platform management, membership editing, trainer approvals, workout library, and system configuration.
            </p>
          </div>
          <div className="p-2 rounded-xl bg-white/5 border border-white/5">
            <span className="font-bold text-emerald-400">Trainer ({trainers.length})</span>
            <p className="text-slate-400 text-[10px] mt-0.5">
              Client management, assigned workout program creation, direct messaging, and session appointments.
            </p>
          </div>
          <div className="p-2 rounded-xl bg-white/5 border border-white/5">
            <span className="font-bold text-slate-300">Member ({members.length})</span>
            <p className="text-slate-400 text-[10px] mt-0.5">
              Workout logging, progress logs, daily check-in, nutrition tracking, and coach communication.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
